import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PipelineStatusBanner } from '../components/PipelineStatusBanner';
import { Surface } from '../components/Surface';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type EstablishmentPoint = { vintageYear: number; totalEstablishments: number };
type PermitPoint = {
  vintageYear: number;
  totalUnits: number | null;
  singleFamilyUnits: number | null;
  multiFamilyUnits: number | null;
};
type AcsDemographicsPoint = {
  surveyYear: number;
  population: number | null;
  medianHouseholdIncome: number | null;
  povertyRate: number | null;
  medianGrossRent: number | null;
};
type AcsEducationPoint = {
  surveyYear: number;
  totalPop25: number;
  pctBachelorsPlus: number | null;
};
type NationalBenchmarkPoint = {
  surveyYear: number;
  nationalMedianIncome: number | null;
  nationalPovertyRate: number | null;
  nationalPctBachelorsPlus: number | null;
};

type Designations = {
  isPersistentPoverty: boolean;
  ersTypology: string | null;
  hasOpportunityZone: boolean;
  hasNmtcEligible: boolean;
};

type CommunityHealth = {
  geography: { countyFips: string; areaName: string } | null;
  laborForce: unknown;
  industries: unknown[];
  education: unknown;
  establishmentTrend: EstablishmentPoint[];
  permitTrend: PermitPoint[];
  acsDemographics: AcsDemographicsPoint[];
  acsEducation: AcsEducationPoint[];
  nationalBenchmarks: NationalBenchmarkPoint[];
  designations: Designations | null;
  citations: Record<string, string>;
  reason: string;
};

function fmt(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtDollars(n: number | null | undefined) {
  if (n == null) return '—';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function NoData({ label }: { label: string }) {
  return (
    <p className="py-4 text-center text-sm text-slate-400">
      No {label} data loaded — run the data pipeline first.
    </p>
  );
}

const BADGE_STYLES: Record<string, string> = {
  'Opportunity Zone': 'bg-emerald-50 text-emerald-800 border-emerald-200',
  'Persistent Poverty County': 'bg-amber-50 text-amber-800 border-amber-200',
  'NMTC Eligible': 'bg-blue-50 text-blue-800 border-blue-200',
};

function DesignationBadge({ label }: { label: string }) {
  const cls = BADGE_STYLES[label] ?? 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

export function CommunityHealthRoute() {
  const getToken = useSessionTokenGetter();
  const [health, setHealth] = useState<CommunityHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [narrative, setNarrative] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function downloadPdf() {
    setDownloading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/community-health/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `community-health-${health?.geography?.countyFips ?? 'export'}-${new Date().getFullYear()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/community-health`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) setFetchError(`API error ${res.status}`);
          return;
        }
        if (!cancelled) {
          setHealth((await res.json()) as CommunityHealth);
          // Fetch narrative async after main data loads
          fetch(`${API}/api/v1/community-health/narrative`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
            .then((d: { narrative: string }) => {
              if (!cancelled && d?.narrative) setNarrative(d.narrative);
            })
            .catch(() => {
              if (!cancelled) setNarrative('');
            });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  if (fetchError) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-2 text-2xl font-bold text-slate-900">Community Health</h1>
        <Surface className="p-6">
          <p className="text-sm text-red-600">
            Failed to load community health data ({fetchError}). Check the API logs.
          </p>
        </Surface>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-sm text-slate-400">Loading community health data…</p>
      </div>
    );
  }

  if (!health?.geography) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="mb-2 text-2xl font-bold text-slate-900">Community Health</h1>
        <Surface className="p-6">
          <p className="text-sm text-amber-700">
            No geography configured. Add a County FIPS code to your default Jurisdiction Profile in{' '}
            <a href="/settings/jurisdiction" className="underline">
              Settings → Jurisdiction
            </a>
            .
          </p>
        </Surface>
      </div>
    );
  }

  const {
    geography,
    establishmentTrend,
    permitTrend,
    acsDemographics,
    acsEducation,
    nationalBenchmarks,
    designations,
    citations,
  } = health;

  // Merge benchmark data into demographic/education arrays by year for dual-line charts
  const benchmarkByYear = new Map(nationalBenchmarks.map((b) => [b.surveyYear, b]));
  const demographicsWithBench = acsDemographics.map((d) => ({
    ...d,
    nationalMedianIncome: benchmarkByYear.get(d.surveyYear)?.nationalMedianIncome ?? null,
    nationalPovertyRate: benchmarkByYear.get(d.surveyYear)?.nationalPovertyRate ?? null,
  }));
  const educationWithBench = acsEducation.map((d) => ({
    ...d,
    nationalPctBachelorsPlus: benchmarkByYear.get(d.surveyYear)?.nationalPctBachelorsPlus ?? null,
  }));

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Community Health</h1>
          <p className="mt-1 text-sm text-slate-500">
            {geography.areaName} · County FIPS {geography.countyFips}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void downloadPdf()}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {downloading ? 'Generating…' : 'Download PDF'}
        </button>
      </div>

      <PipelineStatusBanner />

      {/* AI Narrative Summary */}
      <Surface className="p-5">
        <div className="mb-2">
          <h2 className="text-base font-semibold text-slate-900">Community Overview</h2>
        </div>
        {narrative === null ? (
          <p className="text-sm text-slate-400 italic">Generating summary…</p>
        ) : narrative === '' ? (
          <p className="text-sm text-slate-400">Summary unavailable.</p>
        ) : (
          <p className="text-sm text-slate-700 leading-relaxed">{narrative}</p>
        )}
      </Surface>

      {/* Federal Designations */}
      {designations &&
        (designations.isPersistentPoverty ||
          designations.hasOpportunityZone ||
          designations.hasNmtcEligible ||
          designations.ersTypology) && (
          <Surface className="p-5">
            <div className="mb-3">
              <h2 className="text-base font-semibold text-slate-900">
                Federal &amp; USDA Designations
              </h2>
              <p className="text-xs text-slate-400">
                Special designations that may unlock incentive programs and financing tools
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {designations.hasOpportunityZone && <DesignationBadge label="Opportunity Zone" />}
              {designations.isPersistentPoverty && (
                <DesignationBadge label="Persistent Poverty County" />
              )}
              {designations.hasNmtcEligible && <DesignationBadge label="NMTC Eligible" />}
              {designations.ersTypology && (
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
                  USDA: {designations.ersTypology}
                </span>
              )}
            </div>
          </Surface>
        )}

      {/* 1 — Population Trend */}
      <Surface className="p-5">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-slate-900">Population Trend</h2>
          <p className="text-xs text-slate-400">
            Total county population — Census ACS 5-Year Estimates
          </p>
        </div>
        {acsDemographics.filter((d) => d.population != null).length > 1 ? (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={acsDemographics} margin={{ top: 4, right: 24, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="surveyYear" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 10 }}
                  width={52}
                  domain={['auto', 'auto']}
                />
                <Tooltip formatter={(v) => [fmt(v as number), 'Population']} />
                <Line
                  type="monotone"
                  dataKey="population"
                  stroke="#0d9488"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
            {acsDemographics.length >= 2 &&
              (() => {
                const first = acsDemographics.find((d) => d.population != null);
                const last = [...acsDemographics].reverse().find((d) => d.population != null);
                if (!first || !last || first === last || !first.population || !last.population)
                  return null;
                const change = last.population - first.population;
                const pct = ((change / first.population) * 100).toFixed(1);
                return (
                  <p className="mt-2 text-xs text-slate-500">
                    {first.surveyYear}–{last.surveyYear}:{' '}
                    <span
                      className={
                        change >= 0 ? 'text-teal-700 font-semibold' : 'text-red-600 font-semibold'
                      }
                    >
                      {change >= 0 ? '+' : ''}
                      {fmt(change)} ({change >= 0 ? '+' : ''}
                      {pct}%)
                    </span>
                  </p>
                );
              })()}
          </>
        ) : (
          <NoData label="ACS population" />
        )}
      </Surface>

      {/* 2 — Median Household Income Trend */}
      <Surface className="p-5">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-slate-900">Median Household Income</h2>
          <p className="text-xs text-slate-400">
            Inflation-unadjusted nominal dollars — Census ACS 5-Year Estimates (B19013)
          </p>
        </div>
        {demographicsWithBench.filter((d) => d.medianHouseholdIncome != null).length > 1 ? (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart
                data={demographicsWithBench}
                margin={{ top: 4, right: 24, bottom: 4, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="surveyYear" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 10 }}
                  width={52}
                  domain={['auto', 'auto']}
                />
                <Tooltip formatter={(v) => [fmtDollars(v as number), '']} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="medianHouseholdIncome"
                  name="Local"
                  stroke="#0891b2"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="nationalMedianIncome"
                  name="National median"
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
            {(() => {
              const last = [...acsDemographics]
                .reverse()
                .find((d) => d.medianHouseholdIncome != null);
              const first = acsDemographics.find((d) => d.medianHouseholdIncome != null);
              if (
                !first ||
                !last ||
                first === last ||
                !first.medianHouseholdIncome ||
                !last.medianHouseholdIncome
              )
                return null;
              const change = last.medianHouseholdIncome - first.medianHouseholdIncome;
              const pct = ((change / first.medianHouseholdIncome) * 100).toFixed(1);
              return (
                <p className="mt-2 text-xs text-slate-500">
                  {first.surveyYear}–{last.surveyYear}:{' '}
                  <span
                    className={
                      change >= 0 ? 'text-teal-700 font-semibold' : 'text-red-600 font-semibold'
                    }
                  >
                    {change >= 0 ? '+' : ''}
                    {fmtDollars(change)} ({change >= 0 ? '+' : ''}
                    {pct}%)
                  </span>{' '}
                  · Latest: {fmtDollars(last.medianHouseholdIncome)}
                </p>
              );
            })()}
          </>
        ) : (
          <NoData label="ACS income" />
        )}
      </Surface>

      {/* 3 — Poverty Rate Trend */}
      <Surface className="p-5">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-slate-900">Poverty Rate</h2>
          <p className="text-xs text-slate-400">
            % of population below federal poverty level — Census ACS 5-Year Estimates (B17001)
          </p>
        </div>
        {demographicsWithBench.filter((d) => d.povertyRate != null).length > 1 ? (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart
                data={demographicsWithBench}
                margin={{ top: 4, right: 24, bottom: 4, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="surveyYear" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 10 }}
                  width={42}
                  domain={['auto', 'auto']}
                />
                <Tooltip formatter={(v) => [`${v}%`, '']} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="povertyRate"
                  name="Local"
                  stroke="#dc2626"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="nationalPovertyRate"
                  name="National median"
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
            {(() => {
              const first = acsDemographics.find((d) => d.povertyRate != null);
              const last = [...acsDemographics].reverse().find((d) => d.povertyRate != null);
              if (
                !first ||
                !last ||
                first === last ||
                first.povertyRate == null ||
                last.povertyRate == null
              )
                return null;
              const change = last.povertyRate - first.povertyRate;
              return (
                <p className="mt-2 text-xs text-slate-500">
                  {first.surveyYear}–{last.surveyYear}:{' '}
                  <span
                    className={
                      change <= 0 ? 'text-teal-700 font-semibold' : 'text-red-600 font-semibold'
                    }
                  >
                    {change >= 0 ? '+' : ''}
                    {change.toFixed(1)} pp
                  </span>{' '}
                  · Latest: {last.povertyRate}%
                </p>
              );
            })()}
          </>
        ) : (
          <NoData label="ACS poverty" />
        )}
      </Surface>

      {/* 4 — Median Gross Rent Trend */}
      <Surface className="p-5">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-slate-900">Median Gross Rent</h2>
          <p className="text-xs text-slate-400">
            Median monthly rent including utilities — Census ACS 5-Year Estimates (B25064)
          </p>
        </div>
        {acsDemographics.filter((d) => d.medianGrossRent != null).length > 1 ? (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={acsDemographics} margin={{ top: 4, right: 24, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="surveyYear" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => `$${v}`}
                  tick={{ fontSize: 10 }}
                  width={52}
                  domain={['auto', 'auto']}
                />
                <Tooltip formatter={(v) => [fmtDollars(v as number), 'Median Gross Rent']} />
                <Line
                  type="monotone"
                  dataKey="medianGrossRent"
                  stroke="#7c3aed"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
            {(() => {
              const first = acsDemographics.find((d) => d.medianGrossRent != null);
              const last = [...acsDemographics].reverse().find((d) => d.medianGrossRent != null);
              if (
                !first ||
                !last ||
                first === last ||
                !first.medianGrossRent ||
                !last.medianGrossRent
              )
                return null;
              const change = last.medianGrossRent - first.medianGrossRent;
              const pct = ((change / first.medianGrossRent) * 100).toFixed(1);
              return (
                <p className="mt-2 text-xs text-slate-500">
                  {first.surveyYear}–{last.surveyYear}:{' '}
                  <span
                    className={
                      change >= 0 ? 'text-amber-700 font-semibold' : 'text-teal-700 font-semibold'
                    }
                  >
                    {change >= 0 ? '+' : ''}
                    {fmtDollars(change)} ({change >= 0 ? '+' : ''}
                    {pct}%)
                  </span>{' '}
                  · Latest: {fmtDollars(last.medianGrossRent)}/mo
                </p>
              );
            })()}
          </>
        ) : (
          <NoData label="ACS rent" />
        )}
      </Surface>

      {/* 5 — Educational Attainment Trend */}
      <Surface className="p-5">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-slate-900">Educational Attainment</h2>
          <p className="text-xs text-slate-400">
            % of adults 25+ with bachelor's degree or higher — Census ACS 5-Year Estimates (B15003)
          </p>
        </div>
        {educationWithBench.filter((d) => d.pctBachelorsPlus != null).length > 1 ? (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart
                data={educationWithBench}
                margin={{ top: 4, right: 24, bottom: 4, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="surveyYear" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 10 }}
                  width={42}
                  domain={['auto', 'auto']}
                />
                <Tooltip formatter={(v) => [`${v}%`, '']} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="pctBachelorsPlus"
                  name="Local"
                  stroke="#7c3aed"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="nationalPctBachelorsPlus"
                  name="National median"
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
            {(() => {
              const last = [...acsEducation].reverse().find((d) => d.pctBachelorsPlus != null);
              const first = acsEducation.find((d) => d.pctBachelorsPlus != null);
              if (
                !first ||
                !last ||
                first === last ||
                first.pctBachelorsPlus == null ||
                last.pctBachelorsPlus == null
              )
                return null;
              const change = last.pctBachelorsPlus - first.pctBachelorsPlus;
              return (
                <p className="mt-2 text-xs text-slate-500">
                  {first.surveyYear}–{last.surveyYear}:{' '}
                  <span
                    className={
                      change >= 0 ? 'text-teal-700 font-semibold' : 'text-red-600 font-semibold'
                    }
                  >
                    {change >= 0 ? '+' : ''}
                    {change.toFixed(1)} pp
                  </span>{' '}
                  · Latest: {last.pctBachelorsPlus}% with BA+
                </p>
              );
            })()}
          </>
        ) : (
          <NoData label="ACS education" />
        )}
      </Surface>

      {/* 4 — Business Activity Trend */}
      <Surface className="p-5">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-slate-900">Business Activity Trend</h2>
          <p className="text-xs text-slate-400">
            Total employer establishments, all industries — Census County Business Patterns (CBP)
          </p>
        </div>
        {establishmentTrend.length > 1 ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={establishmentTrend} margin={{ top: 4, right: 24, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="vintageYear" tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
                tick={{ fontSize: 10 }}
                width={48}
                domain={['auto', 'auto']}
              />
              <Tooltip formatter={(v) => [fmt(v as number), 'Establishments']} />
              <Line
                type="monotone"
                dataKey="totalEstablishments"
                stroke="#0d9488"
                strokeWidth={2}
                dot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <NoData label="CBP establishment" />
        )}
      </Surface>

      {/* 5 — Building Permits */}
      <Surface className="p-5">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-slate-900">Building Permits</h2>
          <p className="text-xs text-slate-400">
            Census Building Permits Survey (BPS) — annual housing units authorized
          </p>
        </div>
        {permitTrend.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={permitTrend} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="vintageYear" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} width={40} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="singleFamilyUnits"
                  name="Single-family"
                  stackId="a"
                  fill="#0d9488"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="multiFamilyUnits"
                  name="Multi-family"
                  stackId="a"
                  fill="#0891b2"
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 flex gap-6 text-xs text-slate-500">
              {permitTrend.slice(-1).map((p) => (
                <span key={p.vintageYear}>
                  <span className="font-semibold">{p.vintageYear}:</span> {fmt(p.totalUnits)} total
                  units · {fmt(p.singleFamilyUnits)} single-family · {fmt(p.multiFamilyUnits)}{' '}
                  multi-family
                </span>
              ))}
            </div>
          </>
        ) : (
          <NoData label="BPS permit" />
        )}
      </Surface>

      {/* Citations */}
      <div className="space-y-1">
        {Object.values(citations).map((c) => (
          <p key={c} className="text-[11px] text-slate-400">
            {c}
          </p>
        ))}
      </div>
    </div>
  );
}
